import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as api from "../lib/api.ts";
import { notify, notifyError } from "../lib/notify.ts";

/**
 * Done with the coach or alone, said by hand. A set the coach logs says it
 * already; this is for the rest: a session the coach watched without typing,
 * or one logged in the sheet, so the journal reads true back to the start.
 */
export function SessionCoach({ sessionId, withCoach }: { sessionId: number; withCoach: boolean }) {
  const client = useQueryClient();
  // Shown at once: a queued answer only reaches the server's copy later.
  const [chosen, setChosen] = useState<boolean | null>(null);
  const on = chosen ?? withCoach;

  const toggle = async () => {
    const next = !on;
    setChosen(next);
    try {
      const result = await api.setSessionCoached(sessionId, next);
      navigator.vibrate?.(25);
      if (result.queued) notify("Hors ligne : gardé, envoyé au retour du réseau.");
      else await client.invalidateQueries();
    } catch (error) {
      setChosen(null);
      notifyError("Non enregistré", error);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => void toggle()}
      className="mt-4 flex w-full items-center gap-4 border-t border-line/60 pt-3 text-left"
    >
      <span className="min-w-0 flex-1">
        <span className="label block">Avec coach</span>
        <span className="mt-0.5 block text-[0.78rem] text-ink-mute">{on ? "Faite avec le coach." : "Faite seul."}</span>
      </span>
      <span
        aria-hidden
        className={["relative h-7 w-12 shrink-0 rounded-full transition-colors", on ? "bg-accent" : "bg-line"].join(
          " ",
        )}
      >
        <span
          className={[
            "absolute top-0.5 h-6 w-6 rounded-full transition-[left]",
            on ? "left-[1.375rem] bg-accent-ink" : "left-0.5 bg-ink",
          ].join(" ")}
        />
      </span>
    </button>
  );
}
